/**
 * Error Report Component
 * Floating bubble for error reporting with Telegram integration
 * 
 * Features:
 * - Floating bubble button in corner
 * - Expandable error report form
 * - Submit to Telegram bot
 * - i18n support
 * - Smooth animations
 */

import React, { useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bug as BugIcon, 
  X as XIcon, 
  Send as SendIcon,
  MessageCircle as MessageCircleIcon,
  AlertCircle as AlertCircleIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import DOMPurify from 'isomorphic-dompurify';

interface ErrorReportData {
  description: string;
  email?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  platform?: string;
  videoUrl?: string;
}

const ErrorReport: React.FC = () => {
  const { t } = useTranslation('error-report');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'error'>('idle');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{email?: string; description?: string}>({});
  const formRef = useRef<HTMLFormElement>(null);
  
  // Email validation function
  const isValidEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate inputs
    const newErrors: {email?: string; description?: string} = {};
    
    if (!description.trim()) {
      newErrors.description = t('errors.descriptionRequired');
      setErrors(newErrors);
      return;
    }
    
    if (description.length > 500) {
      newErrors.description = t('errors.descriptionTooLong');
      setErrors(newErrors);
      return;
    }
    
    if (email && !isValidEmail(email)) {
      newErrors.email = t('errors.invalidEmail');
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Sanitize inputs
      const sanitizedDescription = DOMPurify.sanitize(description.trim());
      const sanitizedEmail = email ? DOMPurify.sanitize(email.trim()) : undefined;
      
      const errorData: ErrorReportData = {
        description: sanitizedDescription,
        email: sanitizedEmail,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toLocaleString('vi-VN'),
        platform: navigator.platform,
      };

      // Send to backend API
      const response = await fetch('/api/error-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsOpen(false);
          setIsSubmitted(false);
          setDescription('');
          setEmail('');
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to send report');
      }
    } catch (error) {
      console.error('Error sending report:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const bubbleVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { type: "spring", stiffness: 500, damping: 30 }
    },
    hover: { scale: 1.1 }
  };

  const formVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8, 
      y: 50,
      transition: { duration: 0.2 }
    },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    }
  };

  return (
    <>
      {/* Floating Bubble */}      <motion.div
        className="fixed bottom-6 right-6 z-50 flex flex-col items-center"
        variants={bubbleVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="bg-white text-primary px-3 py-1 rounded-full shadow-sm text-sm font-medium whitespace-nowrap">
            {t('bubbleLabel')}
          </span>
          <Button
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 rounded-full bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-shadow duration-300"
            aria-label={t('bubbleLabel')}
          >
            <MessageCircleIcon className="w-6 h-6" />
          </Button>
        </div>
      </motion.div>

      {/* Error Report Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Header */}              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center">
                    <MessageCircleIcon className="w-4 h-4 text-primary dark:text-primary/90" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('title')}
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <XIcon className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6">
                {isSubmitted ? (
                  <motion.div
                    className="text-center py-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >                    <div className="w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircleIcon className="w-8 h-8 text-primary dark:text-primary/90" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('success.title')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('success.message')}
                    </p>
                  </motion.div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {t('description')}
                      </p>
                    </div>                    {/* Description Field */}
                    <div>
                      <label 
                        htmlFor="error-description" 
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t('form.description')} *
                      </label>
                      <textarea
                        id="error-description"
                        value={description}
                        onChange={(e) => {
                          setDescription(e.target.value);
                          if (errors.description) setErrors({...errors, description: undefined});
                        }}
                        placeholder={t('form.descriptionPlaceholder')}
                        className={`w-full px-3 py-2 border ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                        rows={4}
                        required
                        maxLength={500}
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {description.length}/500
                        </p>
                        {errors.description && (
                          <p className="text-xs text-red-500 flex items-center">
                            <AlertCircleIcon className="w-3 h-3 mr-1" />
                            {errors.description}
                          </p>
                        )}
                      </div>
                    </div>                    {/* Email Field */}
                    <div>
                      <label 
                        htmlFor="error-email" 
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {t('form.email')} ({t('form.optional')})
                      </label>
                      <input
                        id="error-email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors({...errors, email: undefined});
                        }}
                        placeholder={t('form.emailPlaceholder')}
                        className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('form.emailNote')}
                        </p>
                        {errors.email && (
                          <p className="text-xs text-red-500 flex items-center">
                            <AlertCircleIcon className="w-3 h-3 mr-1" />
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Error Message */}
                    {submitStatus === 'error' && (
                      <motion.div
                        className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className="flex items-center space-x-2 text-red-700 dark:text-red-400">
                          <AlertCircleIcon className="w-4 h-4" />
                          <span className="text-sm">{t('errors.submitFailed')}</span>
                        </div>
                      </motion.div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsOpen(false)}
                        disabled={isSubmitting}
                      >
                        {t('form.cancel')}
                      </Button>                      <Button
                        type="submit"
                        disabled={!description.trim() || isSubmitting}
                        className="bg-primary hover:bg-primary/90 text-white"
                      >
                        {isSubmitting ? (
                          <motion.div
                            className="flex items-center space-x-2"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>{t('form.submitting')}</span>
                          </motion.div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <SendIcon className="w-4 h-4" />
                            <span>{t('form.submit')}</span>
                          </div>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ErrorReport;
